import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { getDateModified } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface SignAndStampFile {
  readonly fileId: string;
  readonly inspectionId: string;
  readonly fileUUID: string;
  readonly fileCategory: number;
  readonly drawingType: number;
  readonly isImageFile: boolean;
  readonly pcsId: string;
  readonly sizeMb: number;
  readonly originalPath: string;
  readonly extension: string;
  readonly name: string;
  readonly originalFileName: string;
  readonly allowSpaccess: boolean;
  readonly legacyId: number;
  readonly fkAssetInspectionTask: number;
  readonly siteId: number;
  readonly disciplineId: number;
  readonly fileDescription: string;
  readonly showOnReport: boolean;
  readonly isDelete: string;
  readonly isSaveForFuture: boolean;
  readonly userId: number;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly leadInspectorName?: string;
  readonly leadInspectorTitle?: string;
  readonly leadCompanyName?: string;
  readonly leadDesignation?: string;
  readonly fieldInspectorName?: string;
  readonly fieldInspectorTitle?: string;
  readonly fieldCompanyName?: string;
  readonly fieldDesignation?: string;
}

export interface SignAndStampFileAdditionalFields extends AdditionalFields {
  isBulkOperation?: boolean;
  isLiStamp?: boolean;
}

export const signAndStampFileSchema: RxJsonSchema<SignAndStampFile> = {
  type: 'object',
  version: 1,
  description: 'describes sign and stamp files',
  primaryKey: 'fileUUID',
  properties: {
    fileId: { type: ['string', 'null'] },
    inspectionId: { type: ['string', 'null'] },
    fileUUID: { type: 'string', maxLength: 36 },
    fileCategory: { type: ['number', 'null'] },
    drawingType: { type: ['number', 'null'] },
    isImageFile: { type: ['boolean', 'null'] },
    pcsId: { type: ['string', 'null'] },
    sizeMb: { type: ['number', 'null'] },
    originalPath: { type: ['string', 'null'] },
    extension: { type: ['string', 'null'] },
    name: { type: ['string', 'null'] },
    originalFileName: { type: ['string', 'null'] },
    allowSpaccess: { type: ['boolean', 'null'] },
    legacyId: { type: ['number', 'null'] },
    fkAssetInspectionTask: { type: ['number', 'null'] },
    siteId: { type: ['number', 'null'] },
    disciplineId: { type: ['number', 'null'] },
    fileDescription: { type: ['string', 'null'] },
    showOnReport: { type: ['boolean', 'null'] },
    isDelete: { type: ['string', 'null'] },
    isSaveForFuture: { type: ['boolean', 'null'] },
    userId: { type: ['number', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    leadInspectorName: { type: ['string', 'null'] },
    leadInspectorTitle: { type: ['string', 'null'] },
    leadDesignation: { type: ['string', 'null'] },
    leadCompanyName: { type: ['string', 'null'] },
    fieldInspectorName: { type: ['string', 'null'] },
    fieldInspectorTitle: { type: ['string', 'null'] },
    fieldDesignation: { type: ['string', 'null'] },
    fieldCompanyName: { type: ['string', 'null'] },
  },
};

export const signAndStampFileMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: SignAndStampFileDocument) => {
    const newDoc = {
      ...oldDoc,
    };
    delete newDoc.inspectorId;
    return newDoc;
  },
};

export type SignAndStampFileCollection = RxCollection<SignAndStampFile> | null;
export type SignAndStampFileDocument = RxDocument<SignAndStampFile>;

export const signAndStampFilePullQueryBuilder = (document: SignAndStampFile) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query signAndStampFilesFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastModified: DateTime!
      $isLeadInspector: Boolean!
    ) {
      signAndStampFilesFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastModified: $lastModified
        isLeadInspector: $isLeadInspector
      ) {
        fileId
        inspectionId
        fileUUID
        fileCategory
        drawingType
        isImageFile
        pcsId
        sizeMb
        originalPath
        extension
        name
        originalFileName
        allowSpaccess
        legacyId
        fkAssetInspectionTask
        siteId
        disciplineId
        fileDescription
        showOnReport
        isDelete
        isSaveForFuture
        userId
        dateCreated
        dateModified
        leadInspectorName
        leadInspectorTitle
        leadDesignation
        leadCompanyName
        fieldInspectorName
        fieldInspectorTitle
        fieldDesignation
        fieldCompanyName
      }
    }
  `);
  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastModified: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.siteId,
        doc.disciplineId,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export const signAndStampFilePushQueryBuilder = (doc: SignAndStampFile[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation userSignAndStampFileInfo(
      $MobileUserSignAndStampInfoInput: MobileUserSignAndStampInfoInput
    ) {
      userSignAndStampFileInfo(userFileRequest: $MobileUserSignAndStampInfoInput) {
        fileId
        uUID
        inspectionId
        result
      }
    }
  `);

  const variables = {
    MobileUserSignAndStampInfoInput: document,
  };

  return {
    query,
    variables,
  };
};

export const SIGN_STAMP_BLACK_LISTED_KEYS = [
  '_attachments',
  '_rev',
  '_deleted',
  'dateCreated',
  'dateModified',
  '_meta',
  'isDeleted',
];

export const signAndStampFilePushModifier = (doc: SignAndStampFileDocument) => {
  const obj: SignAndStampFileDocument & SignAndStampFileAdditionalFields = {
    ...doc,
    isBulkOperation: false,
    isLiStamp: doc?.inspectionId?.length && doc?.fkAssetInspectionTask === 0 ? true : false,
  };
  SIGN_STAMP_BLACK_LISTED_KEYS.forEach(property => {
    obj[property as keyof SignAndStampFileAdditionalFields] = undefined;
  });
  return obj;
};

export default {};
